import { DatePipe } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
} from "@angular/core";
import { AbstractControl, FormControl, FormGroup } from "@angular/forms";
import { faAsterisk } from "@fortawesome/free-solid-svg-icons";
import { Placement } from "@ng-bootstrap/ng-bootstrap/util/positioning";
import {
  BenefitPeriod,
  WrittenCertificationV2WithRM,
} from "certification/models/cti";
import { IonicPage, LoadingController, ViewController } from "ionic-angular";
import * as _ from "lodash";
import moment from "moment";
import { Subscription } from "rxjs";
import { AuthService } from "../../../app/auth/auth.service";
import SortOptions from "../../../models/content-wrapper/sort-options";
import PatientChart from "../../../models/patient-chart/patientChartResponse";
import { CredentialedResourcePipe } from "../../../pipes/output-formatters/credentialed-resource.pipe";
import { CareTeamsService } from "../../../providers/care-teams-service/care-teams-service";
import { NavBarServiceProvider } from "../../../providers/nav-bar-service/nav-bar-service";
import { ReferralsServiceProvider } from "../../../providers/patient-charts/referrals-service";
import { ResourceManagementService } from "../../../providers/resource-management/resource-management.service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { Hospice } from "../../models/hospice";
import { CertificationService } from "../../services/certification-service";

@IonicPage()
@Component({
  selector: "written-certification-form",
  templateUrl: "written-certification-form.html",
})
export class WrittenCertificationForm {
  private readonly CERTIFYING_PHYSICIAN_CONTROL_NAME: string =
    "certifyingPhysician";
  private readonly ATTENDING_PHYSICIAN_CONTROL_NAME: string =
    "attendingPhysician";

  /**
   * CO-3354 — the user-editable controls, used to decide whether an edit
   * actually changed anything (see hasUnsavedFieldChanges). An INCLUDE list, not
   * "everything except reasonForChange", so programmatic bookkeeping controls
   * (signatureAttending / attendingSignature, written during load) cannot flip
   * the comparison to "changed" with no user input and defeat the guard.
   */
  private static readonly EDITABLE_CONTROLS = [
    "benefitPeriod",
    "certifyingPhysician",
    "certifyingObtainedOn",
    "attendingPhysician",
    "attendingObtainedOn",
    "narrativeStatement",
    "narrativeOnFile",
  ];

  /** Snapshot of the editable values as loaded; null while creating. */
  private editBaseline: string = null;

  hasValidCertifyingPhysicianName = true;
  hasValidAttendingPhysicianName = true;
  protected hasValidCertifyingUsername = true;
  protected hasValidAttendingUsername = true;
  private physicianData = {};
  private hospice: Hospice;
  private patientChart: PatientChart;
  private briefNarrativeStatement: string;

  public showAttendingPhysicianOptions = false;
  public attendingPhysicianOptions: any[] = [];

  public showCertifyingPhysicianOptions = false;
  public certifyingPhysicianOptions: any[] = [];

  public showBenefitPeriods = false;
  public benefitPeriods: BenefitPeriod[];
  public benefitPeriod: BenefitPeriod;
  public upcomingBenefitPeriod: BenefitPeriod;

  private patientSubscription: Subscription;

  public certTypeControl: FormControl;

  public verbalDisabled = false;
  public writtenDisabled = false;

  public previousCertifyingPhysicianQbosId: number;
  public previousAttendingPhysicianQbosId: number;

  public physicians: any[] = [];
  allPhysicianDisplayNames: string[] = [];
  public disabledBtn = false;
  spinner: any = null;
  spinnerCounter: number = 0;
  selectedPhysician: any;
  patientId: string;
  patientData: any;
  attestationPatient;
  obtainedFromUser: string;
  currentUserFullName: string;
  currentUserQbosId: number;
  usersData: any;
  obtainedFromUserQbosId: number;
  duplicateBenefit: boolean = false;
  id: number = 0;
  benefitPeriodIdOnEdit: number = 0;
  certificationForm: FormGroup;
  currentDate = new Date();
  currentDateFormat = {
    year: this.currentDate.getFullYear(),
    month: this.currentDate.getMonth() + 1,
    day: this.currentDate.getDate(),
  };
  minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 100));
  minDateFormat = {
    year: this.minDate.getFullYear(),
    month: this.minDate.getMonth() + 1,
    day: this.minDate.getDate(),
  };
  maxDate = new Date(new Date().setFullYear(new Date().getFullYear() + 100));
  maxDateFormat = {
    year: this.maxDate.getFullYear(),
    month: this.maxDate.getMonth() + 1,
    day: this.maxDate.getDate(),
  };
  startMinDate: any = this.minDateFormat;
  startMaxDate: any = this.currentDateFormat;
  endMinDate: any = this.currentDateFormat;
  endMaxDate: any = this.maxDateFormat;
  datePlacementPreference: Placement = "left-top";
  disableCertifyingObtainedOn = false;
  requiredIcon = faAsterisk;
  @Input() isOnService: false;
  @Input() origin: string;
  @Input() writtenCertifications: any;
  @Input() allowNarrativeEdit: boolean;
  @Input() type: string;
  @Input() defaultNarrative: string;
  @Input() editData: WrittenCertificationV2WithRM;
  // @Input() workGroups: any[];
  @Input() loggedInUserIsMDorDO: boolean;

  @Output() isValidChanged = new EventEmitter<boolean>();
  @Output() isSubmitted = new EventEmitter();

  attendingSignature = 0;
  certifyingSignature: number = 0;
  isCheckedSignature = false;
  @Input() validateCertifyingPhysician: boolean = false;
  @Input() validateAttendingPhysician: boolean = false;
  userId: number;

  constructor(
    public referralsService: ReferralsServiceProvider,
    private viewCtrl: ViewController,
    private stateData: StateManagementService,
    private renderer: Renderer2,
    private elRef: ElementRef,
    public loadingCtrl: LoadingController,
    private certificationSvc: CertificationService,
    private navBarService: NavBarServiceProvider,
    private datePipe: DatePipe,
    private careTeamService: CareTeamsService,
    public resourceManagementService: ResourceManagementService,
    public credentialedResourcePipe: CredentialedResourcePipe,
    private authSvc: AuthService
  ) {
    this.certTypeControl = new FormControl("");

    this.certificationForm = new FormGroup(
      {
        benefitPeriod: new FormControl(""),
        certifyingPhysician: new FormControl(""),
        certifyingObtainedOn: new FormControl(""),
        attendingPhysician: new FormControl(""),
        attendingObtainedOn: new FormControl(""),
        narrativeStatement: new FormControl(""),
        reasonForChange: new FormControl(""),
        attendingSignature: new FormControl(""),
        signatureAttending: new FormControl(""),
        narrativeOnFile: new FormControl(""),
      },
      {
        validators: [this.validatorRequiredPhysicians],
      }
    );

    this.certificationForm
      .get("certifyingObtainedOn")
      .disable({ emitEvent: false });
    this.certificationForm
      .get("attendingObtainedOn")
      .disable({ emitEvent: false });

    this.certificationForm.statusChanges.subscribe((s) =>
      this.isValidChanged.emit(s == "VALID")
    );

    this.hospice = null;

    this.certificationSvc.getHospiceInfo().subscribe((data) => {
      this.hospice = data;
    });

    this.patientId = this.stateData.getChartId();
    this.patientData = this.stateData.selectedPatientData;
    this.patientChart = this.navBarService.getPatientChart();

    if (this.referralsService.PATIENT_STATUS === "referral") {
      const patientChartInfo = JSON.parse(
        window.sessionStorage.getItem("patientChartInfo")
      );
      if (
        patientChartInfo &&
        patientChartInfo.fullName &&
        patientChartInfo.id
      ) {
        this.attestationPatient = {
          name: patientChartInfo.fullName,
          id: patientChartInfo.id,
        };
      } else if (this.patientChart) {
        this.attestationPatient = {
          name: this.patientChart.patient.fullName,
          id: this.patientChart.externalId,
        };
      }
    } else {
      if (this.patientData) {
        this.attestationPatient = {
          name: this.patientData.firstName + " " + this.patientData.lastName,
          id: this.patientData.externalId,
        };
      } else if (this.patientChart) {
        this.attestationPatient = {
          name: this.patientChart.patient.fullName,
          id: this.patientChart.externalId,
        };
      } else {
        this.patientId = window.location.href.split("/")[5];
        this.patientSubscription = this.careTeamService
          .getPatientInfo(this.navBarService.getExternalId().toString())
          .subscribe((x) => {
            this.patientData = x;
            this.attestationPatient = {
              name: this.patientData.displayName,
              id:
                this.patientData.patientChart === undefined
                  ? 0
                  : this.patientData.patientChart.qbosId,
            };
          });
      }
    }

    this.certificationForm
      .get("attendingPhysician")
      .valueChanges.subscribe((val) => {
        this.searchPhysician(val, "attendingPhysician");

        if (
          val &&
          this.allPhysicianDisplayNames &&
          this.allPhysicianDisplayNames.includes(val)
        ) {
          const matchedPhysician = this.findPhysicianByExactDisplayName(val);
          if (matchedPhysician) {
            this.physicianData["attendingPhysician"] = matchedPhysician;
            this.physicianData[
              "attendingPhysician"
            ].displayName = `${matchedPhysician.firstName} ${matchedPhysician.lastName}`;
            this.setPhysicianOptions([], false, "attendingPhysician");
          }
        } else if (val && val.length === 0) {
          delete this.physicianData["attendingPhysician"];
        }

        if (val && val.length > 0) {
          this.certificationForm
            .get("attendingObtainedOn")
            .enable({ emitEvent: false });
        } else {
          this.certificationForm.get("attendingObtainedOn").setValue("");
          this.certificationForm
            .get("attendingObtainedOn")
            .disable({ emitEvent: false });
        }
      });

    this.certificationForm
      .get("certifyingPhysician")
      .valueChanges.subscribe((val) => {
        this.searchPhysician(val, "certifyingPhysician");

        if (
          val &&
          this.allPhysicianDisplayNames &&
          this.allPhysicianDisplayNames.includes(val)
        ) {
          const matchedPhysician = this.findPhysicianByExactDisplayName(val);
          if (matchedPhysician) {
            this.obtainedFromUserQbosId = matchedPhysician.externalId;
            this.physicianData["certifyingPhysician"] = matchedPhysician;
            this.physicianData[
              "certifyingPhysician"
            ].displayName = `${matchedPhysician.firstName} ${matchedPhysician.lastName}`;
            this.setPhysicianOptions([], false, "certifyingPhysician");
          }
        } else if (val && val.length === 0) {
          delete this.physicianData["certifyingPhysician"];
        }

        if (val && val.length > 0) {
          this.certificationForm
            .get("certifyingObtainedOn")
            .enable({ emitEvent: false });
        } else {
          this.certificationForm.get("certifyingObtainedOn").setValue("");
          this.certificationForm
            .get("certifyingObtainedOn")
            .disable({ emitEvent: false });
        }
      });

    this.certificationForm
      .get("certifyingPhysician")
      .valueChanges.subscribe((val) => {
        this.hasValidCertifyingPhysicianName =
          this.allPhysicianDisplayNames.includes(val);
      });
    this.certificationForm
      .get("attendingPhysician")
      .valueChanges.subscribe((val) => {
        if (val && val.length > 0) {
          this.hasValidAttendingPhysicianName =
            this.allPhysicianDisplayNames.includes(val);
        } else {
          this.hasValidAttendingPhysicianName = true;
        }
      });
  }

  initializeUserData = () => {
    let user = this.authSvc.getUser();
    if (user) {
      // const role = JSON.parse(user.profile.role);
      // this.userId = role[0]['userID'];
      this.userId = user.userId;
    }
  };

  initializeBenefitPeriods(benefitPeriods: BenefitPeriod[]) {
    let currentBenefitPeriod: BenefitPeriod;

    benefitPeriods
      .filter((b) => b.benefitPeriod != 0)
      .forEach((b) => {
        if (this.isCurrentPeriod(b)) {
          currentBenefitPeriod = b;
        }
      });

    if (currentBenefitPeriod) {
      this.upcomingBenefitPeriod = currentBenefitPeriod;
      this.benefitPeriod = currentBenefitPeriod;
      this.certificationForm
        .get("benefitPeriod")
        .setValue(this.formatCertificationValue(currentBenefitPeriod));
    }
  }

  formatCertificationValue(benefit: BenefitPeriod) {
    return `${this.datePipe.transform(
      benefit.startDate,
      "MM/dd/yyyy"
    )} To ${this.datePipe.transform(benefit.endDate, "MM/dd/yyyy")}`;
  }

  isCurrentPeriod(benefitPeriod: BenefitPeriod) {
    var startDate = moment(benefitPeriod.startDate);
    var endDate = moment(benefitPeriod.endDate).add(1, "day"); // To make it end date inclusive
    var now = moment();

    return startDate.isBefore(now) && now.isBefore(endDate);
  }

  validatorRequiredPhysicians = (group: FormGroup) => {
    // Rules for narrative and on file narrative checkbox
    if (
      this.allowNarrativeEdit &&
      group.controls["narrativeStatement"].value == "" &&
      !group.controls["narrativeOnFile"].value
    ) {
      return { notValidNarrativeStatus: true };
    }

    if (!this.loggedInUserIsMDorDO) {
      // User is acting as as non MD
      if (this.editData && this.id > 0) {
        // It means user is editing
        if (!this.reasonForChange) {
          return { notValidReasonOfChangeData: true };
        }
      }
    } else {
      // User is acting as MD
      var validators = [];
      if (
        this.validateCertifyingPhysician &&
        !this.hasValidCertifyingUsername
      ) {
        validators.push({ notValidCertifyingData: true });
      }
      if (this.validateAttendingPhysician && !this.hasValidAttendingUsername) {
        validators.push({ notValidAttendingData: true });
      }
      if (
        group.controls["certifyingObtainedOn"].value == "" ||
        group.controls["certifyingObtainedOn"].value == null
      ) {
        validators.push({ notValidCertifyingData: true });
      }

      if (validators.length !== 0) {
        return validators;
      }
    }

    if (
      !this.hasValidCertifyingPhysicianName ||
      !this.hasValidAttendingPhysicianName
    ) {
      return { notValidCertifyingData: true };
    }

    if (this.id > 0) {
      if (!this.reasonForChange) {
        return { notValidReasonOfChangeData: true };
      }
      // CO-3354 — block an edit that changes nothing. See the matching comment
      // in verbal-certification-form.ts: the mandatory reason for change was
      // being discarded because the audit trail is written per CHANGED field,
      // so a no-op save left no record that the edit happened at all.
      if (!this.hasUnsavedFieldChanges(group)) {
        return { noFieldChangesToSave: true };
      }
    }
    if (
      group.controls["certifyingPhysician"].value == "" ||
      group.controls["certifyingObtainedOn"].value == "" ||
      group.controls["certifyingObtainedOn"].value == null
    ) {
      return { notValidCertifyingData: true };
    } else if (
      group.controls["attendingPhysician"].value == "" ||
      group.controls["attendingObtainedOn"].value == "" ||
      group.controls["attendingObtainedOn"].value == null
    ) {
      if (this.duplicateBenefit) {
        this.checkDuplicateBenefitPeriod();
      }
      if (
        (group.controls["attendingPhysician"].value !== "" &&
          (group.controls["attendingObtainedOn"].value == "" ||
            group.controls["attendingObtainedOn"].value == null)) ||
        (group.controls["attendingPhysician"].value == "" &&
          group.controls["attendingObtainedOn"].value !== "" &&
          group.controls["attendingObtainedOn"].value !== null)
      ) {
        return { notValidReasonOfChangeData: true };
      }
    } else {
      return {};
    }
  };

  protected retrieveUser() {
    this.currentUserFullName = this.authSvc.getUser().name;
    this.currentUserQbosId = this.authSvc.getUser().userId;
  }

  findPhysicianByExactDisplayName(displayName: string) {
    if (!displayName || !this.physicians) {
      return null;
    }

    const matchedPhysician = this.physicians.find((physician) => {
      const physicianDisplayName = physician.credentials
        ? `${physician.lastName}, ${physician.firstName} (${physician.credentials})`
        : `${physician.lastName}, ${physician.firstName}`;
      return physicianDisplayName === displayName;
    });

    if (matchedPhysician) {
      return {
        firstName: matchedPhysician.firstName,
        lastName: matchedPhysician.lastName,
        displayName: matchedPhysician.credentials
          ? `${matchedPhysician.lastName}, ${matchedPhysician.firstName} (${matchedPhysician.credentials})`
          : `${matchedPhysician.lastName}, ${matchedPhysician.firstName}`,
        credentials: matchedPhysician.credentials,
        id: matchedPhysician.id,
        qbosId: matchedPhysician.externalId,
        externalId: matchedPhysician.externalId,
      };
    }

    return null;
  }

  searchPhysician(searchTerm: string, fieldName: string) {
    let options = [];
    let showFlag = false;
    if (searchTerm && searchTerm.length > 2 && this.physicians) {
      showFlag = true;
      options = this.physicians
        .map((physician) => {
          return {
            name:
              (physician.firstName + " " + physician.lastName).toLowerCase() +
              "(" +
              physician.credentials +
              ")",
            firstName: physician.firstName,
            lastName: physician.lastName,
            credentials: physician.credentials,
            id: physician.id,
            qbosId: physician.externalId,
          };
        })
        .filter((data) => {
          return (
            data.name.toLowerCase().indexOf(searchTerm.toLowerCase()) !== -1
          );
        });
      this.setPhysicianOptions(
        _.sortBy(options, "lastName"),
        showFlag,
        fieldName
      );
    } else {
      this.setPhysicianOptions(options, showFlag, fieldName);
    }
  }

  setBenefitPeriod(benefitPeriod: BenefitPeriod) {
    this.benefitPeriod = benefitPeriod;
    this.showBenefitPeriods = false;
    this.certificationForm
      .get("benefitPeriod")
      .setValue(this.formatCertificationValue(benefitPeriod));
    this.checkDuplicateBenefitPeriod();
  }

  setPhysicianOptions(options: string[], showFlag: boolean, fieldName: string) {
    switch (fieldName) {
      case "attendingPhysician":
        this.attendingPhysicianOptions = options;
        this.showAttendingPhysicianOptions = showFlag;
        break;
      case "certifyingPhysician":
        this.certifyingPhysicianOptions = options;
        this.showCertifyingPhysicianOptions = showFlag;
        break;
      default:
    }
  }

  setPhysician(phys, control: string) {
    const physControl = this.certificationForm.get(control);
    if (physControl) {
      phys.credentials
        ? physControl.setValue(
            `${phys.lastName}, ${phys.firstName} (${phys.credentials})`
          )
        : physControl.setValue(`${phys.lastName}, ${phys.firstName}`);
      this.obtainedFromUserQbosId = phys.externalId;
      this.physicianData[control] = phys;
      this.physicianData[
        control
      ].displayName = `${phys.firstName} ${phys.lastName}`;
      this.setPhysicianOptions([], false, control);

      // Determine if the name of the physician is the same as the current UserFullName

      if (control == "certifyingPhysician") {
        this.hasValidCertifyingUsername =
          phys.qbosId == this.previousCertifyingPhysicianQbosId ||
          phys.qbosId == this.userId;
      } else if (control == "attendingPhysician") {
        this.hasValidAttendingUsername =
          phys.qbosId == this.previousAttendingPhysicianQbosId ||
          phys.qbosId == this.userId;
      }

      // if (!this.hasValidCertifyingUsername) {
      //     this.hasValidCertifyingUsername = this.workGroups.some((workgroup) => {
      //         return workgroup == WorkgroupEnum.MEDICAL_DIRECTOR ||
      //             workgroup == WorkgroupEnum.ADMINISTRATOR
      //     })
      // }
      //if(this.editData && this.editData['certifyingObtainedFromQbosId'] != 1){
      //  this.hasValidCertifyingUsername = this.hasValidCertifyingUsername || phys.qbosId == this.previousCertifyingPhysicianQbosId;
      //}
      //}
      if (control === this.CERTIFYING_PHYSICIAN_CONTROL_NAME) {
        if (phys.qbosId == this.currentUserQbosId) {
          this.certifyingSignature = 1;
        } else {
          this.certifyingSignature = 2;
        }
      } else if (control === this.ATTENDING_PHYSICIAN_CONTROL_NAME) {
        if (phys.qbosId == this.currentUserQbosId) {
          this.attendingSignature = 1;
        } else {
          this.attendingSignature = 2;
        }
      }
      physControl.updateValueAndValidity();
    }
  }

  ngOnInit() {
    this.renderer.addClass(
      this.elRef.nativeElement.parentNode,
      "modal-cti-confirmation"
    );
    this.retrieveUser();
    this.initializeUserData();

    this.resourceManagementService
      .getCompanyResourceProvidersAndNonProviders(
        0,
        10000,
        "",
        new SortOptions("last_name", "asc")
      )
      .subscribe((response) => {
        this.physicians = [];
        for (let dt of response) {
          for (let content of dt.content) {
            if (
              content.credentials &&
              (content.credentials.split(",").includes("MD") ||
                content.credentials.split(",").includes("DO"))
            ) {
              this.physicians.push(content);
            }
          }
        }
        this.allPhysicianDisplayNames = this.physicians.map((physician) => {
          return physician.credentials
            ? `${physician.lastName}, ${physician.firstName} (${physician.credentials})`
            : `${physician.lastName}, ${physician.firstName}`;
        });
      });

    this.briefNarrativeStatement = this.defaultNarrative;
    this.checkDuplicateBenefitPeriod();
    if (this.editData && Object.keys(this.editData).length > 0) {
      this.setEditData(this.editData);
    } else {
      this.narrativeOnFileControl.setValue(false);
    }

    if (this.editData && Object.keys(this.editData).length == 0) {
      let todayDate = moment().local(true).format("YYYY-MM-DD");
      this.certificationSvc
        .getMissingPatientBenefitPeriods(this.patientId, todayDate)
        .subscribe(
          (response: any) => {
            this.benefitPeriods = response.filter((b) => b.benefitPeriod != 0);
            this.initializeBenefitPeriods(this.benefitPeriods);
            this.initializeUpcomingBenefits(todayDate);
          },
          (_error) => {
            this.benefitPeriods = [];
            this.initializeBenefitPeriods([]);
            this.initializeUpcomingBenefits(todayDate);
          }
        );
    }

    if (this.defaultNarrative) {
      // If a narrative comes from the previous cti or the current one already has, then disable on file narrative checkbox
      this.narrativeOnFileControl.disable();
    }
  }

  /**
   * CO-3354 — stable snapshot of the user-editable values, for change
   * detection. getRawValue() because obtained-on / benefitPeriod are disabled;
   * null and undefined normalise to "" so a value that merely round-trips
   * through the form does not read as a change.
   */
  private substantiveSnapshot(group: FormGroup): string {
    const raw = group.getRawValue();
    return JSON.stringify(
      WrittenCertificationForm.EDITABLE_CONTROLS.map((name) => {
        const value = raw[name];
        return value === null || value === undefined ? "" : String(value);
      })
    );
  }

  /**
   * CO-3354 — true when the user has actually altered a field since the edit
   * was opened. Always true while creating (no baseline), so the guard only
   * ever applies to edits.
   */
  hasUnsavedFieldChanges(group: FormGroup): boolean {
    if (this.editBaseline === null) {
      return true;
    }
    return this.substantiveSnapshot(group) !== this.editBaseline;
  }

  setEditData(data: WrittenCertificationV2WithRM) {
    this.id = data.id;
    this.certificationForm
      .get("certifyingPhysician")
      .setValue(data.certifyingPhysician);
    if (data.certifyingPhysicianRM) {
      this.certificationForm
        .get("certifyingPhysician")
        .setValue(
          this.credentialedResourcePipe.transform(data.certifyingPhysicianRM)
        );
    }
    this.certificationForm
      .get("certifyingObtainedOn")
      .setValue(data.certifyingReceivedDate);

    // data.certifyingSignature == 1, means physician self-signed
    if (data.certifyingSignature == 1 && !this.loggedInUserIsMDorDO) {
      this.certificationForm
        .get("certifyingPhysician")
        .disable({ emitEvent: false });
      this.certificationForm
        .get("certifyingObtainedOn")
        .disable({ emitEvent: false });
      this.disableCertifyingObtainedOn = true;
    }
    if (data.attendingPhysician) {
      this.certificationForm
        .get("attendingPhysician")
        .setValue(data.attendingPhysician);
      // data.attendingSignature == 1, means physician self-signed
      if (data.attendingSignature == 1 && !this.loggedInUserIsMDorDO) {
        this.certificationForm
          .get("attendingPhysician")
          .disable({ emitEvent: false });
        this.certificationForm
          .get("attendingObtainedOn")
          .disable({ emitEvent: false });
      }
      if (data.attendingPhysicianRM) {
        this.certificationForm
          .get("attendingPhysician")
          .setValue(
            this.credentialedResourcePipe.transform(data.attendingPhysicianRM)
          );
      }
      this.certificationForm
        .get("attendingObtainedOn")
        .setValue(data.attendingReceivedDate);
    }

    this.narrativeOnFileControl.setValue(
      data.narrativeOnFile != undefined ? data.narrativeOnFile : false
    );

    this.origin = data.origin;
    this.benefitPeriodIdOnEdit = data.benefitPeriod;
    const benefitPer = {} as BenefitPeriod;
    benefitPer.id = data.benefitPeriodId;
    benefitPer.benefitPeriod = data.benefitPeriod;
    benefitPer.startDate = data.fromDate;
    benefitPer.endDate = data.toDate;
    this.benefitPeriod = benefitPer;

    this.physicianData["certifyingPhysician"] = {};
    this.physicianData["attendingPhysician"] = {};

    if (this.physicianData["certifyingPhysician"]) {
      this.physicianData["certifyingPhysician"].displayName =
        data.certifyingPhysician;
      this.physicianData["certifyingPhysician"].qbosId =
        data.certifyingObtainedFromQbosId;
      this.physicianData["certifyingPhysician"].id =
        data.certifyingObtainedFromId;
    }

    if (this.physicianData["attendingPhysician"]) {
      this.physicianData["attendingPhysician"].displayName =
        data.attendingPhysician;
      this.physicianData["attendingPhysician"].qbosId =
        data.attendingObtainedFromQbosId;
      this.physicianData["attendingPhysician"].id =
        data.attendingObtainedFromId;
    }

    this.hasValidCertifyingPhysicianName = true;
    this.hasValidAttendingPhysicianName = true;

    if (data.attendingSignature) {
      this.certificationForm
        .get("signatureAttending")
        .setValue(data.attendingSignature);
      this.attendingSignature = data.attendingSignature;
      this.isCheckedSignature = data.attendingSignature == 2;
    } else {
      this.isCheckedSignature = false;
    }
    if (data.certifyingSignature) {
      this.certifyingSignature = data.certifyingSignature;
    }

    //this.hasValidCertifyingUsername = this.userId == data.certifyingObtainedFromQbosId;

    //if(data.attendingSignature != 1 &&  data.certifyingObtainedFromQbosId){ // Comes from IDG and is already signed
    //  this.hasValidCertifyingUsername = true;
    //}

    this.previousCertifyingPhysicianQbosId = data.certifyingObtainedFromQbosId;
    this.previousAttendingPhysicianQbosId = data.attendingObtainedFromQbosId;
    this.type = data.type;
    this.setBenefitPeriod(this.benefitPeriod);
    this.certificationForm.get("benefitPeriod").disable({ emitEvent: false });

    // CO-3354 — capture the loaded values LAST, so hasUnsavedFieldChanges
    // compares against what the user was actually shown.
    this.editBaseline = this.substantiveSnapshot(this.certificationForm);
    this.certificationForm.updateValueAndValidity({ emitEvent: true });
  }

  async onSubmit() {
    let patientAdmitDate =
      this.patientChart && this.patientChart.admittedDatetime
        ? moment(this.patientChart.admittedDatetime).format("YYYY-MM-DD")
        : null;
    const payload = {
      origin: this.origin,
      type: this.type,
      id: this.id,
      certifyingPhysicianName: this.certifyingPhysicianToSave,
      certifyingPhysicianSignedDate: this.certifyingPhysicianSignedDate,
      attendingPhysicianName:
        this.attendingPhysician !== ""
          ? this.attendingPhysicianToSave
          : this.attendingPhysician,
      attendingPhysicianSignedDate: this.attendingPhysicianSignedDate,
      patientQbosId: parseInt(this.patientId),
      narrativeStatement: this.narrativeStatement,
      benefitPeriod: this.benefitPeriod ? this.benefitPeriod.id : null,
      reasonForChange: this.reasonForChange,
      narrativeOnFile: this.narrativeOnFile,
      certifyingObtainedFromQbosId: this.certifyingObtainedFromQbosId,
      certifyingObtainedFromId: this.certifyingObtainedFromId,
      attendingObtainedFromQbosId:
        this.attendingPhysician !== ""
          ? this.attendingObtainedFromQbosId
          : null,
      attendingObtainedFromId:
        this.attendingPhysician !== "" ? this.attendingObtainedFromId : null,
      attendingSignature: this.attendingSignature,
      certifyingSignature: this.certifyingSignature,
      patientAdmitDate,
    };

    if (this.id == 0) {
      delete payload.id;
      delete payload.reasonForChange;
    }

    payload["hospice"] = this.hospice;

    let response: any;
    try {
      if (this.id > 0) {
        if (this.type == "WRITTEN_REFERRAL_V2") {
          response = await this.certificationSvc
            .Referral_UpdateWrittenCtiv2(payload)
            .toPromise();
        } else if (this.type == "WRITTEN_PATIENT_CHART_V2") {
          response = await this.certificationSvc
            .PatientChart_UpdateWrittenCtiv2(payload)
            .toPromise();
        } else {
          return {};
        }
      } else {
        if (this.type == "WRITTEN_REFERRAL_V2") {
          response = await this.certificationSvc
            .Referral_CreateWrittenCTIv2(payload)
            .toPromise();
        } else if (this.type == "WRITTEN_PATIENT_CHART_V2") {
          response = await this.certificationSvc
            .PatientChart_CreateWrittenCTIv2(payload)
            .toPromise();
        } else {
          return {};
        }
      }

      if (response) {
        return {};
      }
    } catch (err) {
      if (err) {
        return { error: err.message };
      }
    }
    return {};
  }

  onDismiss() {
    this.viewCtrl.dismiss({ cancel: true });
    this.disabledBtn = false;
  }

  // incremental loader function
  increaseSpinnerCount(message: string = "Creating Assessment...") {
    this.spinnerCounter++;
    if (this.spinnerCounter && this.spinner == null) {
      this.spinner = this.loadingCtrl.create({
        content: message,
      });
      this.spinner.present();
    }
  }

  // decremental loader function
  decreaseSpinnerCount() {
    this.spinnerCounter--;
    if (this.spinnerCounter <= 0) {
      this.spinner.dismiss();
      this.spinner = null;
    }
  }

  ionViewDidLeave() {
    if (this.patientSubscription) {
      this.patientSubscription.unsubscribe();
    }
  }

  get patientQbosId() {
    return parseInt(this.patientId);
  }

  get certifyingPhysician() {
    return this.certificationForm.get("certifyingPhysician").value;
  }

  get certifyingPhysicianToSave() {
    if (this.physicianData["certifyingPhysician"]) {
      return this.physicianData["certifyingPhysician"].displayName;
    } else {
      return this.certificationForm.get("certifyingPhysician").value;
    }
  }

  get certifyingPhysicianSignedDate() {
    let now = new Date();
    return moment(this.certificationForm.get("certifyingObtainedOn").value)
      .utc()
      .hour(now.getHours())
      .minute(now.getMinutes())
      .seconds(now.getSeconds())
      .toISOString();
  }

  get attendingPhysician() {
    return this.certificationForm.get("attendingPhysician").value;
  }

  get attendingPhysicianToSave() {
    if (this.physicianData["attendingPhysician"]) {
      return this.physicianData["attendingPhysician"].displayName;
    } else {
      return this.certificationForm.get("attendingPhysician").value;
    }
  }

  get attendingPhysicianSignedDate() {
    let now = new Date();
    return moment(this.certificationForm.get("attendingObtainedOn").value)
      .utc()
      .hour(now.getHours())
      .minute(now.getMinutes())
      .seconds(now.getSeconds())
      .toISOString();
  }

  get certifyingObtainedFromQbosId() {
    if (this.physicianData["certifyingPhysician"]) {
      return parseInt(this.physicianData["certifyingPhysician"].qbosId);
    }
  }

  get certifyingObtainedFromId() {
    if (this.physicianData["certifyingPhysician"]) {
      return parseInt(this.physicianData["certifyingPhysician"].id);
    }
  }

  get attendingObtainedFromQbosId() {
    if (this.physicianData["attendingPhysician"]) {
      return parseInt(this.physicianData["attendingPhysician"].qbosId);
    }
  }

  get attendingObtainedFromId() {
    if (this.physicianData["attendingPhysician"]) {
      return parseInt(this.physicianData["attendingPhysician"].id);
    }
  }

  get narrativeStatement(): FormGroup {
    return this.certificationForm.get("narrativeStatement").value;
  }

  get reasonForChange(): FormGroup {
    return this.certificationForm.get("reasonForChange").value;
  }

  get narrativeOnFile(): FormGroup {
    return this.certificationForm.get("narrativeOnFile").value;
  }

  get narrativeOnFileControl(): AbstractControl {
    return this.certificationForm.get("narrativeOnFile");
  }

  checkDuplicateBenefitPeriod() {
    if (
      this.writtenCertifications.length > 0 &&
      this.benefitPeriod &&
      this.benefitPeriod.benefitPeriod
    ) {
      let value = this.writtenCertifications.filter(
        (x) => x.benefitPeriodId == this.benefitPeriod.id
      );
      if (value && value.length > 0) {
        if (this.benefitPeriodIdOnEdit == this.benefitPeriod.benefitPeriod) {
          this.certificationForm.controls["benefitPeriod"].setErrors(null);
          this.duplicateBenefit = false;
        } else {
          this.certificationForm.controls["benefitPeriod"].setErrors({
            duplicateBenefit: true,
          });
          this.duplicateBenefit = true;
        }
      } else {
        this.certificationForm.controls["benefitPeriod"].setErrors(null);
        this.duplicateBenefit = false;
      }
    } else {
      this.certificationForm.controls["benefitPeriod"].setErrors(null);
      this.duplicateBenefit = false;
    }
  }

  checkedHandler(event) {
    this.attendingSignature = event.value ? 2 : 0;
  }

  public updateNarrativeStatement = (event) => {
    this.narrativeOnFileControl.setValue(false);
    if (this.id > 0 && this.userId !== this.previousCertifyingPhysicianQbosId) {
      this.physicianData["certifyingPhysician"].displayName = "";
      this.physicianData["certifyingPhysician"].qbosId = 0;
      this.physicianData["certifyingPhysician"].id = 0;
      this.certificationForm.get("certifyingPhysician").setValue(null);
      this.hasValidCertifyingUsername = false;
      this.certificationForm.get("certifyingObtainedOn").setValue("");
      this.certificationForm
        .get("certifyingObtainedOn")
        .disable({ emitEvent: false });
    }
  };
  public initializeUpcomingBenefits = (todayDate: string) => {
    this.certificationSvc
      .GetCurrentBenefitPeriod(this.patientId, todayDate)
      .subscribe(
        (benefitPeriod: BenefitPeriod) => {
          if (benefitPeriod.benefitPeriod != 0) {
            this.certificationForm
              .get("benefitPeriod")
              .setValue(this.formatCertificationValue(benefitPeriod));
            this.setBenefitPeriod(benefitPeriod);
          }

          const ONE_DAY = 1000 * 60 * 60 * 24;
          const today = new Date(todayDate);
          const endDate = new Date(benefitPeriod.endDate);
          const daysDiff = Math.round(
            Math.abs((endDate.getTime() - today.getTime()) / ONE_DAY)
          );

          if (daysDiff <= 15 && daysDiff >= 0) {
            this.certificationSvc
              .getUpcomingBenefitPeriod(
                this.patientId,
                benefitPeriod.benefitPeriod + 1
              )
              .subscribe((resp: BenefitPeriod) => {
                if (resp && resp.benefitPeriod) {
                  if (
                    !this.writtenCertifications.find(
                      (x) => x.benefitPeriod == resp.benefitPeriod
                    )
                  ) {
                    this.benefitPeriods.push(resp);
                  }
                }
              });
          }
        },
        (error) => {
          console.error(error);
        }
      );
  };
}



// WEBPACK FOOTER //
// ./src/certification/forms/written-certification-form/written-certification-form.ts